/**
 * Module dependencies.
 */

import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from 'app/lib/utils';
import { MenuItem } from 'app/types/navigation';
import { menuConfig } from '../dashboard-header';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { useMe } from 'app/hooks/requests/use-me';
import { NavItem, matchPath } from './nav-item';
import { UserMenu } from './user-menu';

/**
 * `Props` type.
 */

type Props = {
  menu: MenuItem[];
  mobileOpen: boolean;
  onNavigate: () => void;
};

/**
 * Settings entry key inside `menuConfig`.
 */

const SETTINGS_KEY = '7';

/**
 * Flatten a menu tree.
 */

function flatten(items: MenuItem[]): MenuItem[] {
  return items.flatMap(item => [item, ...flatten(item.children ?? [])]);
}

/**
 * Resolve the key of the deepest item matching the current path.
 */

function longestMatchKey(pathname: string, items: MenuItem[]): string | undefined {
  const match = flatten(items)
    .filter(item => matchPath(pathname, item.pathname))
    .sort((a, b) => (b.pathname?.length ?? 0) - (a.pathname?.length ?? 0))[0];

  return match?.key as string | undefined;
}

/**
 * `GroupLabel` component.
 */

function GroupLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className={'px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70'}>
      {children}
    </div>
  );
}

/**
 * Export `AppSidebar` component.
 */

export function AppSidebar({ menu, mobileOpen, onNavigate }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { pathname } = useLocation();
  const activeProductKey = useMemo(() => longestMatchKey(pathname, menuConfig), [pathname]);
  const activeSectionKey = useMemo(() => longestMatchKey(pathname, menu), [menu, pathname]);
  const user = useMemo(() => cookieStorage.get('user') as any, []);
  const { data: me } = useMe();
  const isAdmin = !!me?.isAdmin;
  const features = me?.features;

  const products = useMemo(
    () =>
      menuConfig.filter(
        item =>
          item.key !== SETTINGS_KEY &&
          (!item.adminOnly || isAdmin) &&
          // Feature-flagged products are hidden unless enabled for this user.
          (!item.feature || !!features?.[item.feature])
      ),
    [isAdmin, features]
  );
  const activeProduct = useMemo(() => menuConfig.find(item => item.key === activeProductKey), [activeProductKey]);

  // Prefer the live `me` query (updated on profile edits) so the avatar/name
  // refresh immediately; fall back to the cookie.
  const source: any = me ?? user;
  const name: string = [source?.firstName, source?.lastName].filter(Boolean).join(' ') || source?.name || 'Utilizador';
  const email: string = source?.email ?? '';
  const photo: string | null = source?.profilePhotoUrl ?? null;
  const initials = useMemo(() => {
    const base: string = [source?.firstName, source?.lastName].filter(Boolean).join(' ') || source?.email || 'U';

    return base
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]?.toUpperCase())
      .join('');
  }, [source]);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex w-[250px] flex-col overflow-x-hidden bg-muted transition-transform duration-300 ease-in-out',
        // The drawer shadow is mobile-only; md:shadow-none stops it leaking onto
        // desktop when the provider sets `collapsed` on certain routes.
        'md:static md:translate-x-0 md:shadow-none',
        mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
      )}
    >
      {/* Brand */}
      <div className={'flex items-center gap-2.5 px-4 py-4'}>
        <div
          className={
            'grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-primary to-primary/70 text-sm font-bold text-primary-foreground shadow-sm'
          }
        >
          {'C'}
        </div>

        <div className={'min-w-0'}>
          <div className={'truncate text-sm font-semibold leading-tight'}>{'Contab'}</div>

          <div className={'truncate text-[11px] text-muted-foreground'}>{translate('menu.tagline')}</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className={'flex-1 space-y-5 overflow-y-auto overflow-x-hidden px-3 py-1'}>
        <div className={'space-y-0.5'}>
          <GroupLabel>{translate('menu.groups.general')}</GroupLabel>

          {products.map(item => (
            <NavItem
              activeKey={activeProductKey}
              item={{ ...(item as MenuItem), label: translate(item.label as string) }}
              key={item.key}
              onNavigate={onNavigate}
              pathname={pathname}
            />
          ))}
        </div>

        {menu.length > 1 && (
          <div className={'space-y-0.5'}>
            <GroupLabel>
              {activeProduct ? translate(activeProduct.label as string) : translate('menu.groups.section')}
            </GroupLabel>

            {menu.map(item => (
              <NavItem
                activeKey={activeSectionKey}
                item={item}
                key={item.key}
                onNavigate={onNavigate}
                pathname={pathname}
              />
            ))}
          </div>
        )}
      </nav>

      {/* Footer — separated from the nav, holds the user menu */}
      <div className={'p-2'}>
        <UserMenu email={email} initials={initials} name={name} photo={photo} />
      </div>
    </aside>
  );
}
